import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PackagePlus, Plus, Trash2, Boxes, Warehouse, Layers, ArrowRight } from "lucide-react";

// ------------------- Guided Receive Drawer -------------------
function GuidedReceiveSheet({ po, open, onOpenChange, bins, onCompleted }) {
    // allocations state: { [product_id]: [{bin_code, quantity}] }
    const [alloc, setAlloc] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open && po) {
            const initial = {};
            for (const line of po.items) {
                initial[line.product_id] = [{ bin_code: "", quantity: line.quantity }];
            }
            setAlloc(initial);
            setError("");
        }
    }, [open, po]);

    if (!po) return null;

    const setLineAllocs = (pid, next) => setAlloc((a) => ({ ...a, [pid]: next }));
    const addSplit = (pid) => setLineAllocs(pid, [...(alloc[pid] || []), { bin_code: "", quantity: 0 }]);
    const removeSplit = (pid, i) => setLineAllocs(pid, (alloc[pid] || []).filter((_, x) => x !== i));
    const patchSplit = (pid, i, patch) => setLineAllocs(pid, (alloc[pid] || []).map((a, x) => (x === i ? { ...a, ...patch } : a)));

    const isValid = po.items.every((line) => {
        const allocs = alloc[line.product_id] || [];
        if (allocs.length === 0) return false;
        const sum = allocs.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
        const allBins = allocs.every((a) => (a.bin_code || "").trim().length > 0);
        return allBins && Math.abs(sum - Number(line.quantity)) < 0.001;
    });

    const submit = async () => {
        setSubmitting(true);
        setError("");
        try {
            const body = {
                lines: po.items.map((line) => ({
                    product_id: line.product_id,
                    allocations: (alloc[line.product_id] || []).map((a) => ({
                        bin_code: a.bin_code.trim().toUpperCase(),
                        quantity: Number(a.quantity),
                    })),
                })),
            };
            const { data } = await api.post(`/warehouse/purchase-orders/${po.id}/receive-guided`, body);
            toast.success(`Received ${data?.item?.number || "PO"} into bins.`);
            onOpenChange(false);
            onCompleted?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl p-0 gap-0" data-testid="guided-receive-sheet">
                <DialogHeader className="px-6 py-5 border-b border-zinc-200 space-y-1">
                    <DialogTitle className="font-display text-lg tracking-tight">Guided Receive · {po.number}</DialogTitle>
                    <DialogDescription>
                        {po.supplier_name} · {po.items.length} line{po.items.length > 1 ? "s" : ""} · ₹{Number(po.grand_total).toLocaleString("en-IN")}
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto px-6 py-5 space-y-6">
                    {po.items.map((line, idx) => {
                        const allocs = alloc[line.product_id] || [];
                        const sum = allocs.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
                        const remaining = Number(line.quantity) - sum;
                        return (
                            <div key={line.product_id} className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={`gr-line-${idx}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{line.sku}</div>
                                        <div className="text-sm font-medium text-zinc-900">{line.name}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-zinc-400">Ordered</div>
                                        <div className="font-mono text-sm">{line.quantity} {line.unit}</div>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-2">
                                    {allocs.map((a, i) => (
                                        <div key={i} className="flex items-center gap-2" data-testid={`gr-alloc-${idx}-${i}`}>
                                            <Input
                                                list={`bin-list-${idx}-${i}`}
                                                placeholder="Bin code (e.g. A-01)"
                                                value={a.bin_code}
                                                onChange={(e) => patchSplit(line.product_id, i, { bin_code: e.target.value.toUpperCase() })}
                                                className="h-9 font-mono text-sm uppercase"
                                                data-testid={`gr-bin-input-${idx}-${i}`}
                                            />
                                            <datalist id={`bin-list-${idx}-${i}`}>
                                                {bins.map((b) => <option key={b.id} value={b.code} />)}
                                            </datalist>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="any"
                                                value={a.quantity}
                                                onChange={(e) => patchSplit(line.product_id, i, { quantity: e.target.value })}
                                                className="h-9 w-28 text-right font-mono text-sm"
                                                data-testid={`gr-qty-input-${idx}-${i}`}
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeSplit(line.product_id, i)}
                                                disabled={allocs.length === 1}
                                                className="h-9 w-9"
                                                data-testid={`gr-remove-${idx}-${i}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                            </Button>
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between pt-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => addSplit(line.product_id)}
                                            className="h-7 text-xs text-zinc-500 hover:text-zinc-900"
                                            data-testid={`gr-split-${idx}`}
                                        >
                                            <Plus className="mr-1 h-3 w-3" /> Split to another bin
                                        </Button>
                                        <div className={`text-xs font-mono ${Math.abs(remaining) < 0.001 ? "text-emerald-600" : "text-amber-600"}`} data-testid={`gr-remaining-${idx}`}>
                                            {Math.abs(remaining) < 0.001 ? "✓ Fully allocated" : `${remaining > 0 ? "" : "+"}${(-remaining).toFixed(2)} to allocate`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {error && (
                    <div className="mx-6 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="gr-error">
                        {error}
                    </div>
                )}
                <DialogFooter className="border-t border-zinc-200 px-6 py-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gr-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={!isValid || submitting} data-testid="gr-submit">
                        {submitting ? "Receiving…" : "Receive into bins"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ------------------- Bin Capacity Inline Editor -------------------
function CapacityCell({ bin, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(bin.capacity ?? "");
    const save = async () => {
        const cap = value === "" ? null : Number(value);
        try {
            await api.patch(`/bins/${bin.id}`, { capacity: cap });
            toast.success(`Capacity updated for ${bin.code}`);
            setEditing(false);
            onSaved?.();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    if (!editing) return (
        <button onClick={() => setEditing(true)} className="text-xs font-mono text-zinc-500 hover:text-zinc-900" data-testid={`bin-cap-btn-${bin.code}`}>
            {bin.capacity == null ? "— set capacity" : `${bin.capacity}`}
        </button>
    );
    return (
        <div className="flex items-center gap-1">
            <Input autoFocus type="number" min="0" step="any" value={value} onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                className="h-7 w-24 text-xs" data-testid={`bin-cap-input-${bin.code}`} />
            <Button size="sm" variant="outline" onClick={save} className="h-7 px-2 text-xs" data-testid={`bin-cap-save-${bin.code}`}>Save</Button>
        </div>
    );
}

// ------------------- Main Page -------------------
export default function WarehouseHub() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState("receiving");
    const [queue, setQueue] = useState([]);
    const [bins, setBins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPO, setSelectedPO] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    // Create-bin composer
    const [newBinCode, setNewBinCode] = useState("");
    const [newBinCap, setNewBinCap] = useState("");

    const loadAll = async () => {
        setLoading(true);
        try {
            const [q, b] = await Promise.all([
                api.get("/warehouse/receiving-queue"),
                api.get("/bins"),
            ]);
            setQueue(q.data.items || []);
            setBins(b.data.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadAll(); }, []);

    // Deep-link: /warehouse-hub?po=<id> opens the guided receive sheet
    useEffect(() => {
        const poId = searchParams.get("po");
        if (poId && queue.length > 0) {
            const found = queue.find((p) => p.id === poId);
            if (found) {
                setSelectedPO(found);
                setSheetOpen(true);
                setTab("receiving");
                // clear query param so it doesn't re-trigger
                searchParams.delete("po");
                setSearchParams(searchParams, { replace: true });
            }
        }
    }, [queue, searchParams, setSearchParams]);

    const openReceive = (po) => { setSelectedPO(po); setSheetOpen(true); };

    const createBin = async () => {
        const code = newBinCode.trim().toUpperCase();
        if (!code) return;
        try {
            await api.post("/bins", { code, capacity: newBinCap === "" ? null : Number(newBinCap) });
            toast.success(`Bin ${code} created.`);
            setNewBinCode(""); setNewBinCap("");
            loadAll();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const totalUsage = bins.reduce((s, b) => s + Number(b.usage || 0), 0);
    const totalCap = bins.reduce((s, b) => s + (b.capacity ? Number(b.capacity) : 0), 0);

    return (
        <div data-testid="page-warehouse-hub">
            <PageHeader
                title="Warehouse Hub"
                description="Bin-level receiving and inventory location control. Split incoming stock across bins with capacity enforcement."
            />

            <div className="p-8 max-w-[1400px]">
                {/* Summary strip */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                    <div className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500"><Warehouse className="h-3 w-3" strokeWidth={1.75} /> Awaiting receipt</div>
                        <div className="mt-2 text-2xl font-display tracking-tight text-zinc-950" data-testid="wh-stat-queue">{queue.length}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">confirmed POs</div>
                    </div>
                    <div className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500"><Layers className="h-3 w-3" strokeWidth={1.75} /> Active bins</div>
                        <div className="mt-2 text-2xl font-display tracking-tight text-zinc-950" data-testid="wh-stat-bins">{bins.length}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{totalUsage.toFixed(0)} units stored</div>
                    </div>
                    <div className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500"><Boxes className="h-3 w-3" strokeWidth={1.75} /> Capacity</div>
                        <div className="mt-2 text-2xl font-display tracking-tight text-zinc-950">{totalCap > 0 ? `${((totalUsage / totalCap) * 100).toFixed(0)}%` : "—"}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{totalCap > 0 ? `${totalUsage.toFixed(0)} / ${totalCap.toFixed(0)} tracked` : "No caps set"}</div>
                    </div>
                </div>

                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList data-testid="wh-tabs">
                        <TabsTrigger value="receiving" data-testid="wh-tab-receiving">Receiving Queue</TabsTrigger>
                        <TabsTrigger value="bins" data-testid="wh-tab-bins">Bins</TabsTrigger>
                    </TabsList>

                    <TabsContent value="receiving" className="mt-4">
                        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                            <Table>
                                <TableHeader><TableRow className="bg-zinc-50/60">
                                    {["PO", "Supplier", "Lines", "Total", "Confirmed", ""].map((h) => (
                                        <TableHead key={h} className={`text-[10px] uppercase tracking-widest text-zinc-500 ${h === "Total" ? "text-right" : ""}`}>{h}</TableHead>
                                    ))}
                                </TableRow></TableHeader>
                                <TableBody>
                                    {loading ? <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                                    : queue.length === 0 ? <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">No confirmed POs waiting to be received.</TableCell></TableRow>
                                    : queue.map((po) => (
                                        <TableRow key={po.id} data-testid={`wh-queue-row-${po.id}`}>
                                            <TableCell><span className="font-mono text-xs">{po.number}</span></TableCell>
                                            <TableCell><div className="text-sm font-medium">{po.supplier_name}</div></TableCell>
                                            <TableCell><span className="text-xs text-zinc-600">{po.items.length}</span></TableCell>
                                            <TableCell className="text-right font-mono text-xs">₹{Number(po.grand_total).toLocaleString("en-IN")}</TableCell>
                                            <TableCell className="text-xs text-zinc-500 font-mono">{po.confirmed_at ? new Date(po.confirmed_at).toLocaleDateString() : "—"}</TableCell>
                                            <TableCell className="text-right">
                                                <Button size="sm" variant="outline" onClick={() => openReceive(po)} className="h-8 gap-1.5" data-testid={`wh-receive-${po.id}`}>
                                                    <PackagePlus className="h-3.5 w-3.5" strokeWidth={1.5} /> Guided Receive <ArrowRight className="h-3 w-3" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="bins" className="mt-4 space-y-4">
                        {/* Create bin */}
                        <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4">
                            <Label className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">New bin</Label>
                            <div className="mt-2 flex flex-wrap items-end gap-2">
                                <Input placeholder="Code (A-01)" value={newBinCode} onChange={(e) => setNewBinCode(e.target.value.toUpperCase())} className="h-9 w-40 font-mono uppercase" data-testid="wh-newbin-code" />
                                <Input placeholder="Capacity (optional)" type="number" value={newBinCap} onChange={(e) => setNewBinCap(e.target.value)} className="h-9 w-40" data-testid="wh-newbin-cap" />
                                <Button size="sm" onClick={createBin} disabled={!newBinCode.trim()} data-testid="wh-newbin-create">
                                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Create bin
                                </Button>
                                <div className="ml-auto text-[11px] text-zinc-500">
                                    Or just type a new bin code during Guided Receive — it will be created automatically.
                                </div>
                            </div>
                        </div>

                        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                            <Table>
                                <TableHeader><TableRow className="bg-zinc-50/60">
                                    {["Bin", "Usage", "Capacity", "Utilisation", "Created"].map((h) => (
                                        <TableHead key={h} className="text-[10px] uppercase tracking-widest text-zinc-500">{h}</TableHead>
                                    ))}
                                </TableRow></TableHeader>
                                <TableBody>
                                    {loading ? <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                                    : bins.length === 0 ? <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">No bins yet. Create one above, or start a Guided Receive.</TableCell></TableRow>
                                    : bins.map((b) => (
                                        <TableRow key={b.id} data-testid={`wh-bin-row-${b.code}`}>
                                            <TableCell><span className="font-mono text-sm font-medium">{b.code}</span></TableCell>
                                            <TableCell><span className="font-mono text-xs">{Number(b.usage || 0).toLocaleString("en-IN")}</span></TableCell>
                                            <TableCell><CapacityCell bin={b} onSaved={loadAll} /></TableCell>
                                            <TableCell>
                                                {b.utilisation != null ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-zinc-100">
                                                            <div className={`h-full rounded-full ${b.utilisation > 90 ? "bg-red-500" : b.utilisation > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(b.utilisation, 100)}%` }} />
                                                        </div>
                                                        <span className="text-[11px] font-mono text-zinc-500">{b.utilisation}%</span>
                                                    </div>
                                                ) : <span className="text-xs text-zinc-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-xs text-zinc-500 font-mono">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <GuidedReceiveSheet
                po={selectedPO}
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                bins={bins}
                onCompleted={loadAll}
            />
        </div>
    );
}
